import { type CardDetailsWidgetProps } from '@fruitful/types';
import React from 'react';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    CreditCardIcon,
    FootnoteRegular,
    Icon,
    LabelRegular,
    XStack,
    YStack,
} from 'smoothie';

import {
    type TranscriptBankAccount,
    type TranscriptCard,
    type TranscriptCardHolder,
} from '../../utils/transcriptWidgetSources';

interface ResolvedCard {
    account: TranscriptBankAccount;
    card: TranscriptCard;
    /** False when the member's holder record wasn't found on the account. */
    isHolderConfirmed: boolean;
}

const findAccount = (
    accounts: TranscriptBankAccount[],
    { bankAccountId, cardId }: CardDetailsWidgetProps,
): TranscriptBankAccount | null =>
    (bankAccountId != null
        ? accounts.find((account) => account.bankAccountId === bankAccountId)
        : undefined) ??
    (cardId != null
        ? accounts.find((account) =>
              account.cards.some((card) => card.id === cardId),
          )
        : undefined) ??
    null;

const findMemberHolder = (
    account: TranscriptBankAccount,
    memberUserId: string | null | undefined,
): TranscriptCardHolder | null =>
    memberUserId == null
        ? null
        : ([account.primaryUser, account.jointUser].find(
              (holder) => holder?.id === memberUserId,
          ) ?? null);

/**
 * Mirrors the member app: the member's own non-terminated card on the
 * resolved account, preferring the requested card, then an active one.
 */
const resolveCard = (
    accounts: TranscriptBankAccount[],
    props: CardDetailsWidgetProps,
    memberUserId: string | null | undefined,
): ResolvedCard | null => {
    const account = findAccount(accounts, props);
    if (account == null) {
        return null;
    }

    const holder = findMemberHolder(account, memberUserId);
    const candidates = account.cards.filter(
        (card) =>
            card.status !== 'TERMINATED' &&
            (holder?.syncteraPersonId == null ||
                card.syncteraPersonId === holder.syncteraPersonId),
    );
    const card =
        candidates.find((candidate) => candidate.id === props.cardId) ??
        candidates.find((candidate) => candidate.status === 'ACTIVE') ??
        candidates[0];

    return card == null
        ? null
        : { account, card, isHolderConfirmed: holder != null };
};

const MaskedField = ({ label, value }: { label: string; value: string }) => (
    <YStack gap={2}>
        <FootnoteRegular color="$gray900">{label}</FootnoteRegular>
        <LabelRegular color="$black" fontFamily="$monoBody">
            {value}
        </LabelRegular>
    </YStack>
);

/** Read-only, always-masked card tile; the full number, expiration, and CVV are never stored. */
export const TranscriptCardDetails = ({
    props,
    accounts,
    memberUserId,
}: {
    props: CardDetailsWidgetProps;
    accounts: TranscriptBankAccount[];
    memberUserId?: string | null;
}) => {
    const resolved = resolveCard(accounts, props, memberUserId);
    if (resolved == null) {
        return (
            <YStack padding={16} alignItems="center">
                <BodySmallRegular color="$gray1000">
                    No card found.
                </BodySmallRegular>
            </YStack>
        );
    }

    const { account, card, isHolderConfirmed } = resolved;
    const isJoint = account.jointUser != null;

    return (
        <YStack gap={12} padding={16}>
            <XStack gap={12} alignItems="center">
                <Icon color="$black" icon={CreditCardIcon} size={24} />
                <YStack flex={1}>
                    <BodyMediumEmphasized color="$black">
                        {`${account.nickname ?? 'Fruitful'} Card`}
                    </BodyMediumEmphasized>
                    <FootnoteRegular color="$gray1000">
                        {`${card.form === 'VIRTUAL' ? 'Virtual' : 'Physical'} card`}
                    </FootnoteRegular>
                </YStack>
            </XStack>
            <MaskedField
                label="Card number"
                value={`**** **** **** ${card.lastFour ?? '****'}`}
            />
            <XStack gap={24}>
                <MaskedField label="Exp" value="**/**" />
                <MaskedField label="CVV" value="***" />
            </XStack>
            {isJoint && !isHolderConfirmed ? (
                <FootnoteRegular color="$orange1000">
                    Joint account: couldn&apos;t confirm which holder is this
                    member, so this may be the other holder&apos;s card.
                </FootnoteRegular>
            ) : null}
        </YStack>
    );
};
